<template>
    <div class="lista-prompts" :class="{ 'modo-pagina': modoPagina }">
        <!-- Toggle para mobile -->
        <div class="filtros-mobile-toggle d-md-none" @click="filtrosVisiveis = !filtrosVisiveis">
            <span>
                <i class="fa fa-filter mr-2"></i>
                Filtros e categorias
            </span>
            <i :class="filtrosVisiveis ? 'fa fa-chevron-up' : 'fa fa-chevron-down'"></i>
        </div>
        
        <!-- Tabs e Categorias (colapsável no mobile) -->
        <div :class="['filtros-wrapper', { 'd-none': !filtrosVisiveis && isMobile }]">
            <!-- Busca (modo página) -->
            <div v-if="modoPagina" class="search-wrapper-pagina">
                <i class="fa fa-search search-icon"></i>
                <input 
                    v-model="termoBusca" 
                    type="text" 
                    class="form-control search-input" 
                    placeholder="Buscar modelos por nome ou descrição..."
                    @input="debounceBusca"
                >
            </div>
            
            <!-- Tabs -->
            <div class="tabs-wrapper">
                <div class="tabs-left">
                    <button 
                        v-for="(tab, index) in tabs" 
                        :key="tab.value"
                        class="tab-btn"
                        :class="{ 
                            active: abaAtiva === index,
                            'tab-favoritos': tab.value === 'favoritos'
                        }"
                        @click="selecionarAba(index)"
                    >
                        <i v-if="tab.value === 'favoritos'" class="fa fa-star mr-1"></i>
                        {{ tab.label }}
                        <span v-if="getTabCount(tab.value) > 0" class="tab-badge">
                            {{ formatCount(getTabCount(tab.value)) }}
                        </span>
                    </button>
                </div>
                <button 
                    v-if="modoPagina"
                    class="tab-btn tab-novo"
                    @click="$emit('novo-modelo')"
                >
                    <i class="fa fa-plus mr-1"></i>
                    Novo modelo
                </button>
            </div>
                
            <!-- Filtro de categorias (múltipla seleção) -->
            <div class="categorias-wrapper">
                <div class="categorias-scroll">
                    <label class="categoria-checkbox" :class="{ checked: todasSelecionadas }">
                        <input 
                            type="checkbox" 
                            :checked="todasSelecionadas"
                            @change="toggleTodas"
                        >
                        <span class="checkmark"></span>
                        Todas
                    </label>
                    <label 
                        v-for="cat in categorias" 
                        :key="cat.value"
                        class="categoria-checkbox"
                        :class="{ checked: categoriasSelecionadas.includes(cat.value) }"
                    >
                        <input 
                            type="checkbox" 
                            :value="cat.value"
                            v-model="categoriasSelecionadas"
                            @change="onCategoriaChange"
                        >
                        <span class="checkmark"></span>
                        {{ cat.label }}
                    </label>
                </div>
            </div>
        </div>
            
        <!-- Grid de prompts -->
        <div class="prompts-grid" :class="{ 'prompts-grid-pagina': modoPagina }">
            <!-- Loading -->
            <div v-if="carregando" class="empty-state">
                <i class="fa fa-spinner fa-spin fa-3x mb-3"></i>
                <p>Carregando modelos...</p>
            </div>
            
            <div v-else-if="promptsExibidos.length === 0" class="empty-state">
                <i class="fa fa-inbox fa-3x mb-3"></i>
                <p>Nenhum modelo encontrado</p>
                <small class="text-muted">Tente ajustar os filtros ou buscar por outro termo</small>
            </div>
            
            <div 
                v-for="prompt in promptsExibidos" 
                :key="prompt.id"
                class="prompt-card"
            >
                <div class="prompt-card-icon">
                    <i :class="getCategoriaIcon(prompt.categoria)"></i>
                </div>
                <div class="prompt-card-content">
                    <div class="prompt-card-categoria">
                        <span 
                            v-for="cat in getCategoriasArray(prompt)" 
                            :key="cat"
                            class="categoria-badge"
                        >
                            {{ getCategoriaLabel(cat) }}
                        </span>
                    </div>
                    <div class="prompt-card-title">{{ prompt.nome || prompt.titulo }}</div>
                    <div class="prompt-card-desc">"{{ prompt.comando || prompt.descricao }}"</div>
                </div>
                <div class="prompt-card-actions">
                    <button 
                        class="btn-favorito"
                        :class="{ favorito: prompt.favorito || prompt.is_favorito }"
                        @click.stop="confirmarToggleFavorito(prompt)"
                        :title="(prompt.favorito || prompt.is_favorito) ? 'Remover dos favoritos' : 'Adicionar aos favoritos'"
                    >
                        <i :class="(prompt.favorito || prompt.is_favorito) ? 'fa fa-star' : 'fa fa-star-o'"></i>
                    </button>
                    
                    <button 
                        v-if="podeEditar()"
                        class="btn-editar"
                        @click.stop="editarPrompt(prompt)"
                        title="Editar modelo"
                    >
                        <i class="fa fa-pencil"></i>
                    </button>
                    
                    <button 
                        class="btn-usar"
                        @click.stop="usarPrompt(prompt)"
                        title="Usar modelo"
                    >
                        <i class="fa fa-play"></i>
                    </button>
                </div>
            </div>
        </div>
        
        <!-- Paginação -->
        <div v-if="totalPaginas > 1" class="paginacao-wrapper">
            <b-pagination
                v-model="paginaAtual"
                :total-rows="totalRegistros"
                :per-page="porPagina"
                align="center"
                size="md"
                @change="onPaginaChange"
            ></b-pagination>
        </div>
    </div>
</template>

<script>
import { create } from "@/shared/services/api.js"

export default {
    name: 'ListaPrompts',
    
    props: {
        modoPagina: {
            type: Boolean,
            default: false
        },
        termoBuscaExterno: {
            type: String,
            default: ''
        }
    },
    
    data() {
        return {
            apiIA: null,
            abaAtiva: 0,
            termoBusca: '',
            categoriasSelecionadas: [],
            carregando: false,
            filtrosVisiveis: false,
            isMobile: false,
            debounceTimer: null,
            tabs: [
                { value: 'favoritos', label: 'Favoritos' },
                { value: 'recomendados', label: 'Indicações de modelos' }
            ],
            categorias: [
                { value: 'financeiro', label: 'Financeiro', icon: 'fa fa-money' },
                { value: 'fiscal', label: 'Fiscal', icon: 'fa fa-file-text-o' },
                { value: 'vendas', label: 'Vendas', icon: 'fa fa-shopping-cart' },
                { value: 'atendimento', label: 'Atendimento', icon: 'fa fa-headphones' },
                { value: 'estoque', label: 'Estoque / Operações', icon: 'fa fa-archive' },
                { value: 'estrategia', label: 'Estratégia / Gestão', icon: 'fa fa-line-chart' }
            ],
            // Dados
            prompts: [],
            contagens: {
                favoritos: 0,
                recomendados: 0
            },
            // Paginação
            paginaAtual: 1,
            porPagina: 12,
            totalRegistros: 0
        }
    },
    
    computed: {
        todasSelecionadas() {
            return this.categoriasSelecionadas.length === 0
        },
        
        promptsExibidos() {
            return this.prompts
        },
        
        totalPaginas() {
            return Math.ceil(this.totalRegistros / this.porPagina)
        },
        
        tipoAtual() {
            const tipos = ['favoritos', 'recomendados']
            return tipos[this.abaAtiva]
        }
    },
    
    watch: {
        termoBuscaExterno(val) {
            this.termoBusca = val
            this.carregarPrompts()
        }
    },
    
    created() {
        const apiUrl = process.env.VUE_APP_API_IA_URL || 'https://ia.click.app/api'
        // intranet (FastAPI) espera JSON, não multipart
        this.apiIA = create(apiUrl, 'application/json')
    },
    
    mounted() {
        this.checkMobile()
        window.addEventListener('resize', this.checkMobile)
        this.lerQueryString()
        this.carregarPrompts()
    },
    
    beforeDestroy() {
        window.removeEventListener('resize', this.checkMobile)
        if (this.debounceTimer) {
            clearTimeout(this.debounceTimer)
        }
    },
    
    methods: {
        checkMobile() {
            this.isMobile = window.innerWidth < 768
        },
        
        lerQueryString() {
            if (!this.modoPagina) return
            
            const query = this.$route.query
            
            if (query.page) {
                this.paginaAtual = parseInt(query.page) || 1
            }
            
            if (query.busca) {
                this.termoBusca = query.busca
            }
            
            if (query.tipo) {
                const tipos = ['favoritos', 'recomendados']
                const index = tipos.indexOf(query.tipo)
                if (index >= 0) this.abaAtiva = index
            }
            
            if (query.categorias) {
                this.categoriasSelecionadas = Array.isArray(query.categorias) 
                    ? query.categorias 
                    : [query.categorias]
            }
        },
        
        atualizarQueryString() {
            if (!this.modoPagina) return
            
            const query = {}
            
            if (this.paginaAtual > 1) {
                query.page = this.paginaAtual
            }
            
            if (this.termoBusca) {
                query.busca = this.termoBusca
            }
            
            const tipos = ['favoritos', 'recomendados']
            if (this.abaAtiva > 0) {
                query.tipo = tipos[this.abaAtiva]
            }
            
            if (this.categoriasSelecionadas.length > 0) {
                query.categorias = this.categoriasSelecionadas
            }
            
            const currentQuery = JSON.stringify(this.$route.query)
            const newQuery = JSON.stringify(query)
            
            if (currentQuery !== newQuery) {
                this.$router.replace({ query }).catch(() => {})
            }
        },
        
        async carregarPrompts() {
            this.carregando = true
            try {
                const params = new URLSearchParams()
                params.append('page', this.paginaAtual)
                params.append('per_page', this.porPagina)
                params.append('tipo', this.tipoAtual)
                
                if (this.termoBusca) {
                    params.append('busca', this.termoBusca)
                }
                
                if (this.categoriasSelecionadas.length > 0) {
                    this.categoriasSelecionadas.forEach(cat => params.append('categorias[]', cat))
                }
                
                const response = await this.apiIA.get('/v1/prompts?' + params.toString())
                
                if (response && response.code === 200) {
                    const data = response.data
                    
                    // Se API retorna paginado
                    if (data.data) {
                        this.prompts = data.data.map(this.normalizarPrompt)
                        this.totalRegistros = data.total || 0
                    } else {
                        // Fallback para API antiga
                        const promptsAba = data[this.tipoAtual] || []
                        this.prompts = promptsAba.map(this.normalizarPrompt)
                        this.totalRegistros = this.prompts.length
                    }
                    
                    // Atualiza contagens
                    if (data.contagens) {
                        this.contagens = data.contagens
                    } else {
                        this.contagens = {
                            favoritos: data.favoritos?.length || 0,
                            recomendados: data.recomendados?.length || 0
                        }
                    }
                    
                    // Se não tem favoritos e está na aba favoritos, vai para recomendados
                    if (this.abaAtiva === 0 && this.prompts.length === 0 && this.contagens.recomendados > 0) {
                        this.abaAtiva = 1
                        this.carregarPrompts()
                        return
                    }
                }
            } catch (error) {
                console.error('Erro ao carregar prompts:', error)
                this.$notify.error('Erro ao carregar modelos')
            } finally {
                this.carregando = false
            }
        },
        
        normalizarPrompt(prompt) {
            return {
                id: prompt.id,
                nome: prompt.titulo || prompt.nome,
                titulo: prompt.titulo || prompt.nome,
                descricao: prompt.descricao,
                comando: prompt.comando,
                categoria: Array.isArray(prompt.categorias) ? prompt.categorias[0] : prompt.categoria,
                categorias: prompt.categorias || [prompt.categoria],
                favorito: prompt.is_favorito || prompt.favorito || false,
                is_favorito: prompt.is_favorito || prompt.favorito || false,
                is_public: prompt.is_public,
                uso_count: prompt.uso_count || 0,
                usuario_id: prompt.usuario_id,
                empresa_id: prompt.empresa_id
            }
        },
        
        selecionarAba(index) {
            this.abaAtiva = index
            this.paginaAtual = 1
            this.atualizarQueryString()
            this.carregarPrompts()
        },
        
        toggleTodas() {
            this.categoriasSelecionadas = []
            this.paginaAtual = 1
            this.atualizarQueryString()
            this.carregarPrompts()
        },
        
        onCategoriaChange() {
            this.paginaAtual = 1
            this.atualizarQueryString()
            this.carregarPrompts()
        },
        
        onPaginaChange(page) {
            this.paginaAtual = page
            this.atualizarQueryString()
            this.carregarPrompts()
        },
        
        debounceBusca() {
            if (this.debounceTimer) {
                clearTimeout(this.debounceTimer)
            }
            this.debounceTimer = setTimeout(() => {
                this.paginaAtual = 1
                this.atualizarQueryString()
                this.carregarPrompts()
            }, 300)
        },
        
        getTabCount(tabValue) {
            return this.contagens[tabValue] || 0
        },
        
        formatCount(count) {
            return count > 99 ? '99+' : count
        },
        
        getCategoriaIcon(categoria) {
            const cat = this.categorias.find(c => c.value === categoria)
            return cat ? cat.icon : 'fa fa-tag'
        },
        
        getCategoriaLabel(categoria) {
            const cat = this.categorias.find(c => c.value === categoria)
            return cat ? cat.label : categoria || 'Geral'
        },
        
        getCategoriasArray(prompt) {
            if (Array.isArray(prompt.categorias) && prompt.categorias.length) {
                return prompt.categorias
            }
            if (prompt.categoria) {
                return [prompt.categoria]
            }
            return []
        },
        
        podeEditar() {
            // Pode editar qualquer prompt (próprios ou criar cópia)
            return true
        },
        
        async confirmarToggleFavorito(prompt) {
            const isFavorito = prompt.favorito || prompt.is_favorito
            
            if (isFavorito) {
                // Confirmar remoção
                const confirmacao = await this.$dialog.confirm(
                    'Deseja remover este modelo dos seus favoritos?',
                    {
                        icon: 'fa fa-star text-warning'
                    }
                )
                if (confirmacao) {
                    await this.toggleFavorito(prompt)
                }
            } else {
                // Confirmar adição
                const confirmacao = await this.$dialog.confirm(
                    'Deseja adicionar este modelo aos seus favoritos?',
                    {
                        icon: 'fa fa-star text-warning'
                    }
                )
                if (confirmacao) {
                    await this.toggleFavorito(prompt)
                }
            }
        },
        
        async toggleFavorito(prompt) {
            const novoEstado = !(prompt.favorito || prompt.is_favorito)
            
            try {
                if (novoEstado) {
                    await this.apiIA.post('/v1/prompts/' + prompt.id + '/favoritar')
                    this.$notify.success('Adicionado aos favoritos!')
                } else {
                    if (prompt.usuario_id != null) {
                        await this.apiIA.delete('/v1/prompts/' + prompt.id)
                        this.$notify.info('Modelo removido')
                    } else {
                        await this.apiIA.delete('/v1/prompts/' + prompt.id + '/favoritar')
                        this.$notify.info('Removido dos favoritos')
                    }
                }
                
                await this.carregarPrompts()
            } catch (error) {
                console.error('Erro ao alterar favorito:', error)
                this.$notify.error('Erro ao alterar favorito')
            }
        },
        
        async usarPrompt(prompt) {
            try {
                await this.apiIA.post('/v1/prompts/' + prompt.id + '/usar')
            } catch (e) {
                // Ignora erro de registro de uso
            }
            
            this.$emit('usar-prompt', prompt.comando)
        },
        
        editarPrompt(prompt) {
            this.$emit('editar-prompt', prompt)
        },
        
        // Método público para recarregar
        recarregar() {
            this.carregarPrompts()
        }
    }
}
</script>

<style scoped>
.lista-prompts {
    background: #fff;
}

/* Busca para modo página */
.search-wrapper-pagina {
    position: relative;
    padding: 16px 20px 0;
}

.search-wrapper-pagina .search-icon {
    position: absolute;
    left: 34px;
    top: 50%;
    transform: translateY(-50%);
    margin-top: 8px;
    color: #999;
}

.search-wrapper-pagina .search-input {
    padding-left: 40px;
    border-radius: 8px;
    border: 1px solid #e0e0e0;
    background: #f8f9fa;
    height: 42px;
}

.search-wrapper-pagina .search-input:focus {
    background: #fff;
    border-color: rgb(36, 61, 75);
    box-shadow: 0 0 0 2px rgba(36, 61, 75, 0.1);
}

/* Toggle de filtros para mobile */
.filtros-mobile-toggle {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 20px;
    background: #f8f9fa;
    border-bottom: 1px solid #eee;
    cursor: pointer;
    font-size: 0.9rem;
    color: #666;
    transition: all 0.2s;
}

.filtros-mobile-toggle:hover {
    background: #f0f0f0;
    color: #333;
}

.filtros-mobile-toggle .fa-filter {
    color: rgb(36, 61, 75);
}

.filtros-wrapper {
    transition: all 0.3s ease;
}

/* Tabs */
.tabs-wrapper {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
    padding: 16px 20px;
    border-bottom: 1px solid #eee;
    background: #fff;
    flex-wrap: wrap;
}

.tabs-left {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
}

.tab-btn {
    padding: 8px 16px;
    border: none;
    background: transparent;
    color: #666;
    font-size: 0.9rem;
    border-radius: 20px;
    cursor: pointer;
    transition: all 0.2s;
}

.tab-btn:hover {
    background: #f0f0f0;
    color: #333;
}

.tab-btn.active {
    background: rgb(36, 61, 75);
    color: #fff;
}

.tab-btn.tab-favoritos {
    color: #f5a623;
    border: 1px solid #e09000;
}

.tab-btn.tab-favoritos:hover {
    background: rgba(245, 166, 35, 0.1);
    color: #e09000;
}

.tab-btn.tab-favoritos.active {
    background: #f5a623;
    color: #fff;
    border-color: #f5a623;
}

.tab-btn.tab-favoritos .fa-star {
    color: inherit;
}

.tab-btn.tab-novo {
    background: #00a65a;
    color: #fff;
    border: none;
}

.tab-btn.tab-novo:hover {
    background: #008d4c;
    color: #fff;
}

.tab-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 20px;
    height: 20px;
    padding: 0 6px;
    margin-left: 6px;
    font-size: 0.75rem;
    font-weight: 600;
    background: rgba(0, 0, 0, 0.1);
    border-radius: 10px;
}

.tab-btn.active .tab-badge {
    background: rgba(255, 255, 255, 0.25);
}

.tab-btn.tab-favoritos .tab-badge {
    background: rgba(245, 166, 35, 0.2);
}

.tab-btn.tab-favoritos.active .tab-badge {
    background: rgba(255, 255, 255, 0.3);
}

/* Categorias */
.categorias-wrapper {
    padding: 12px 20px;
    background: #fafafa;
    border-bottom: 1px solid #eee;
}

.categorias-scroll {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.categoria-checkbox {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    background: #fff;
    border: 1px solid #e0e0e0;
    border-radius: 20px;
    cursor: pointer;
    font-size: 0.85rem;
    color: #666;
    transition: all 0.2s;
    margin: 0;
}

.categoria-checkbox:hover {
    border-color: rgb(36, 61, 75);
    color: rgb(36, 61, 75);
}

.categoria-checkbox.checked {
    background: rgb(36, 61, 75);
    border-color: rgb(36, 61, 75);
    color: #fff;
}

.categoria-checkbox input {
    display: none;
}

.categoria-checkbox .checkmark {
    display: none;
}

/* Grid de prompts */
.prompts-grid {
    padding: 16px 20px;
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
    max-height: 400px;
    overflow-y: auto;
}

.prompts-grid-pagina {
    max-height: none;
    grid-template-columns: repeat(2, 1fr);
}

@media (max-width: 768px) {
    .prompts-grid {
        grid-template-columns: 1fr;
    }
    
    .prompts-grid-pagina {
        grid-template-columns: 1fr;
    }
    
    .tabs-wrapper {
        flex-direction: column;
        gap: 4px;
    }
    
    .tab-btn {
        width: 100%;
        text-align: left;
    }
    
    .categorias-scroll {
        flex-wrap: wrap;
    }
}

.empty-state {
    grid-column: 1 / -1;
    text-align: center;
    padding: 40px;
    color: #999;
}

/* Card de prompt */
.prompt-card {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 14px 16px;
    background: #fff;
    border: 1px solid #e8e8e8;
    border-radius: 12px;
    transition: all 0.2s;
    overflow: hidden;
}


.prompt-card:hover {
    border-color: rgb(36, 61, 75);
    box-shadow: 0 2px 8px rgba(0,0,0,0.08);
    transform: translateY(-1px);
}

.prompt-card-icon {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    background: rgb(36, 61, 75);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.prompt-card-icon i {
    font-size: 1rem;
    color: #fff;
}

.prompt-card-content {
    flex: 1;
    min-width: 0;
}

.prompt-card-categoria {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    margin-bottom: 2px;
}

.categoria-badge {
    font-size: 0.7rem;
    font-weight: 600;
    color: #888;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    background: none;
    padding: 0;
}

.prompt-card-title {
    font-weight: 600;
    font-size: 0.9rem;
    color: #333;
    margin-bottom: 4px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.prompt-card-desc {
    font-size: 0.85rem;
    color: #333;
    font-style: italic;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.prompt-card-actions {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    gap: 4px;
    flex-shrink: 0;
}

.btn-favorito,
.btn-editar,
.btn-usar {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.05);
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
}

.btn-favorito {
    color: #ccc;
}

.btn-favorito:hover {
    background: rgba(255, 193, 7, 0.15);
    color: #ffc107;
}

.btn-favorito.favorito {
    color: #ffc107;
    background: rgba(255, 193, 7, 0.15);
}

.btn-editar {
    color: #999;
}

.btn-editar:hover {
    background: rgba(36, 61, 75, 0.1);
    color: rgb(36, 61, 75);
}

.btn-usar {
    color: #00a65a;
    background: rgba(0, 166, 90, 0.1);
}

.btn-usar:hover {
    background: #00a65a;
    color: #fff;
}

/* Paginação */
.paginacao-wrapper {
    padding: 16px 20px;
    border-top: 1px solid #eee;
    background: #fafafa;
}

/* Modo página */
.modo-pagina {
    border-radius: 8px;
    border: 1px solid #e8e8e8;
    overflow: hidden;
}

/* .modo-pagina .prompts-grid {
    min-height: 400px;
} */
</style>

